import { Button } from '@base-ui/react/button';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_LABELS } from '@/admin/languages';
import { MutationError } from '@/admin/sitecontent/errors';
import { HelpArticleForm } from '@/admin/sitecontent/HelpArticleForm';
import { invalidateHelp } from '@/admin/sitecontent/invalidate';
import {
  HeaderActions,
  HeaderRow,
  StatusMessage,
} from '@/admin/sitecontent/StatusMessage';
import buttonStyles from '@/admin/styles/button.module.css';
import { Badge } from '@/admin/ui/Badge';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import { DetailPage } from '@/admin/ui/Editor';
import { useEditMode, validateEditSearch } from '@/admin/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import { BackLink } from '@/admin/ui/NavLinks';
import { PageTitle } from '@/admin/ui/Page';
import { RichContent } from '@/admin/ui/RichContent';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import api from '@/api';

export const Route = createFileRoute('/admin/help/$articleId')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: validateEditSearch,
  component: HelpArticleDetail,
});

function HelpArticleDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { articleId } = Route.useParams();
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const article = api.getAdminHelpArticle.useQuery({
    path: { article_id: Number(articleId) },
  });
  const remove = api.deleteAdminHelpArticle.useMutation(undefined, {
    onSuccess: () => {
      invalidateHelp();
      navigate({ to: '/admin/help/' });
    },
  });

  if (article.isLoading)
    return <StatusMessage>{t('admin.help.loadingArticle')}</StatusMessage>;
  if (article.error || !article.data)
    return (
      <StatusMessage $error>
        {t('admin.help.errorLoadingArticle')}
      </StatusMessage>
    );
  const data = article.data;

  return (
    <DetailPage>
      <BackLink>
        <Link to="/admin/help/">{t('admin.help.backToArticles')}</Link>
      </BackLink>
      <HeaderRow>
        <PageTitle>{data.title}</PageTitle>
        {!editing && (
          <HeaderActions>
            <Button className={buttonStyles.Button} onClick={startEditing}>
              {t('admin.common.edit')}
            </Button>
            <Button
              className={buttonStyles.Button}
              onClick={() => setConfirmingDelete(true)}
            >
              {t('admin.common.delete')}
            </Button>
          </HeaderActions>
        )}
      </HeaderRow>

      {editing ? (
        <HelpArticleForm
          key={data.id}
          article={data}
          onSaved={stopEditing}
          onCancel={stopEditing}
        />
      ) : (
        <>
          <Section>
            <SectionTitle>{t('admin.common.details')}</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>{t('admin.common.id')}</FieldLabel>
                <FieldValue>{data.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.help.section')}</FieldLabel>
                <FieldValue>{data.section_name}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.language')}</FieldLabel>
                <FieldValue>{t(LANGUAGE_LABELS[data.language])}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.status')}</FieldLabel>
                <FieldValue>
                  <Badge $variant={data.is_published ? 'success' : 'neutral'}>
                    {data.is_published
                      ? t('admin.help.statusPublished')
                      : t('admin.help.statusDraft')}
                  </Badge>{' '}
                  {data.needs_rewrite && (
                    <Badge $variant="warning">
                      {t('admin.help.needsRewrite')}
                    </Badge>
                  )}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.order')}</FieldLabel>
                <FieldValue>{data.sort_order}</FieldValue>
              </Field>
            </FieldGrid>
          </Section>
          <Section>
            <SectionTitle>{t('admin.help.body')}</SectionTitle>
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: admin view of backend-authored help HTML */}
            <RichContent dangerouslySetInnerHTML={{ __html: data.content }} />
          </Section>
        </>
      )}

      <ConfirmDialog
        open={confirmingDelete}
        title={t('admin.help.deleteArticleTitle')}
        confirmLabel={t('admin.common.delete')}
        tone="danger"
        pending={remove.isPending}
        onConfirm={() => remove.mutate({ path: { article_id: data.id } })}
        onCancel={() => setConfirmingDelete(false)}
      >
        <p>{t('admin.help.deleteArticleBody')}</p>
        <MutationError area="help" error={remove.error} />
      </ConfirmDialog>
    </DetailPage>
  );
}
